import { sttStatusLabel } from '../types/stt-status';
/**
 * Responses component created by karora on 26/05/17.
 */
import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Observable, Observer } from 'rxjs';

import { ProfileService } from '../services/profile.service';
import { VoxPopService } from '../services/voxpop.service';
import { VoxResponse } from '../types/response';
import { Topic } from '../types/topic';

@Component({
  selector: 'responses',
  templateUrl: './responses.component.html',
  styleUrls: ['./responses.component.css'],
  providers: [VoxPopService],
  standalone: false
})


export class ResponsesComponent implements OnInit {
  allResponses: VoxResponse[] = [];
  topic_id: number;
  programme_id: number;
  person_id: number;
  before: number;
  beforeResponses: number[];
  @Input() topic: Topic;
  private _showAll: boolean = false;
  loading = false;
  responses: VoxResponse[] = [];
  currentResponse: VoxResponse;
  currentResponseObservable: Observable<VoxResponse>;
  emitter: Observer<VoxResponse>;
  errorMessage = '';
  sortMode = 'received-desc';
  statusFilter = 'all';
  publishMessage: string | null = null;
  publishMessageIsError = false;
  publishMessageResponseId: number | null = null;
  private publishingResponses: { [responseId: number]: boolean } = {};
  private publishTargets: { [responseId: number]: boolean } = {};

  constructor(
    private voxPopService: VoxPopService,
    private route: ActivatedRoute,
    private router: Router,
  ) {
  }

  ngOnInit(): void {
    if (!ProfileService.IsLoggedIn()) return;
    this.currentResponseObservable = Observable.create((e: Observer<VoxResponse>) => this.emitter = e);
    this.loading = true;
    this.route.params.subscribe((params: Params) => this.loadResponses(params, true));
  }

  get showAll(): boolean {
    return this._showAll;
  }

  @Input()
  set showAll(show: boolean) {
    this._showAll = show;
    if (this.allResponses) {
      this.refreshVisibleResponses();
    }
  }

  refreshResponses(): void {
    this.loadResponses(this.route.snapshot.params, false);
  }

  getResponses(params: Params): Observable<VoxResponse[]> {
    this.topic_id = +params['topicid'];
    this.programme_id = +params['programmeid'];
    this.person_id = +params['personid'];
    return this.voxPopService.getResponses(this.programme_id, this.topic_id, this.person_id, this.before);
  }

  private loadResponses(params: Params, resetPagination: boolean): void {
    if (resetPagination) {
      this.before = 0;
      this.beforeResponses = [];
      this.currentResponse = null;
    }
    this.loading = true;
    this.errorMessage = '';
    this.getResponses(params).subscribe(
      responses => this.gotResponses(responses),
      error => this.failResponses(error),
    );
  }

  private failResponses(error: any): void {
    this.loading = false;
    this.responses = [];
    this.allResponses = [];
    this.errorMessage = typeof error === 'string' ? error : (error && error.message ? error.message : 'Unable to load responses.');
  }

  gotResponses(responses: VoxResponse[]) {
    console.log("Got " + responses.length + " responses, min duration " + (this.topic && this.topic.min_duration ? this.topic.min_duration : "???"));
    this.loading = false;
    this.allResponses = responses;
    this.refreshVisibleResponses();
  }

  private refreshVisibleResponses(): void {
    let visibleResponses: VoxResponse[] = this.allResponses ? this.allResponses.slice() : [];
    if (!this._showAll) {
      visibleResponses = visibleResponses.filter((response) => {
        if (response.transcript == '[No speech detected]') return false;
        if (this.topic && response.duration < this.topic.min_duration) return false;
        return true;
      });
    }

    if (this.statusFilter === 'published') {
      visibleResponses = visibleResponses.filter((response) => response.published);
    } else if (this.statusFilter === 'draft') {
      visibleResponses = visibleResponses.filter((response) => !response.published);
    }

    visibleResponses.sort((left, right) => this.compareResponses(left, right));
    this.responses = visibleResponses;
  }

  private compareResponses(left: VoxResponse, right: VoxResponse): number {
    const leftTime = Date.parse(left.received);
    const rightTime = Date.parse(right.received);
    const leftStamp = isNaN(leftTime) ? left.id : leftTime;
    const rightStamp = isNaN(rightTime) ? right.id : rightTime;
    if (this.sortMode === 'received-asc') {
      return leftStamp - rightStamp;
    }
    return rightStamp - leftStamp;
  }

  onSelect(response: VoxResponse): void {
    this.currentResponse = response;
    try {
      this.emitter.next(response);
    } catch (e) {
      console.log("Response.onSelect: " + e.message);
    }
  }

  cancelPlayer(cancel: boolean): void {
    this.onSelect(null);
  }

  responseUpdated(updated: VoxResponse): void {
    this.currentResponse = updated;
    this.refreshResponses();
  }

  transcriptUpdated(updated: VoxResponse): void {
    this.currentResponse = updated;
    this.allResponses = this.allResponses.map((response) =>
      response.id === updated.id ? updated : response
    );
    this.refreshVisibleResponses();
  }

  publishResponse(response: VoxResponse): void {
    if (!response || this.isPublishing(response)) return;
    let toPublish = this.currentResponse;
    if (!toPublish || response.id != toPublish.id) {
      toPublish = response;
    }
    const targetPublished = !toPublish.published;
    const publishUpdate: VoxResponse = {
      ...toPublish,
      published: targetPublished,
    };
    this.publishingResponses[toPublish.id] = true;
    this.publishTargets[toPublish.id] = targetPublished;
    this.publishMessage = null;
    this.publishMessageIsError = false;
    this.publishMessageResponseId = null;
    console.log("Publish " + (targetPublished ? "on" : "off") + " for " + toPublish.id);
    this.voxPopService.updateResponse(publishUpdate, true).subscribe(
      (updated) => this.publishSucceeded(updated),
      () => this.publishFailed(toPublish, targetPublished),
    );
  }

  isPublishing(response: VoxResponse): boolean {
    return !!response && !!this.publishingResponses[response.id];
  }

  publishActionLabel(response: VoxResponse): string {
    if (this.isPublishing(response)) {
      return this.publishTargets[response.id] ? 'Publishing...' : 'Unpublishing...';
    }
    return response.published ? 'Unpublish' : 'Publish';
  }

  publishActionTitle(response: VoxResponse): string {
    if (this.isPublishing(response)) {
      return this.publishTargets[response.id] ? 'Publishing response' : 'Unpublishing response';
    }
    return response.published ? 'Unpublish response' : 'Publish response';
  }

  private publishSucceeded(updated: VoxResponse): void {
    this.clearPublishProgress(updated.id);
    this.publishMessageIsError = false;
    this.publishMessage = updated.published ? 'Response published.' : 'Response unpublished and returned to draft.';
    this.publishMessageResponseId = updated.id;
    this.currentResponse = this.currentResponse && this.currentResponse.id === updated.id
      ? updated
      : this.currentResponse;
    this.allResponses = this.allResponses.map((response) =>
      response.id === updated.id ? updated : response
    );
    this.refreshVisibleResponses();
  }

  private publishFailed(response: VoxResponse, targetPublished: boolean): void {
    this.clearPublishProgress(response.id);
    this.publishMessageIsError = true;
    this.publishMessageResponseId = response.id;
    this.publishMessage = targetPublished
      ? 'Publish failed. The response remains a draft. Please try again.'
      : 'Unpublish failed. The response remains published. Please try again.';
  }

  private clearPublishProgress(responseId: number): void {
    delete this.publishingResponses[responseId];
    delete this.publishTargets[responseId];
  }

  personResponses(response: VoxResponse): void {
    this.router.navigate(['/review/responses/person/', response.person_id]);
  }

  getColorFor(response: VoxResponse): string {
    if (this.currentResponse === response) return '#f8f8f8';
    if (this.errorMessage) return '#ff8080'; // So editor doesn't make me think this can be static
    return '#EEEEEE';
  }

  toggleShowAll(): void {
    this._showAll = !this._showAll;
    this.refreshVisibleResponses();
  }

  onFilterChange(): void {
    this.refreshVisibleResponses();
  }

  resetFilters(): void {
    this._showAll = false;
    this.statusFilter = 'all';
    this.sortMode = 'received-desc';
    this.refreshVisibleResponses();
  }

  get hasActiveFilters(): boolean {
    return this._showAll || this.statusFilter !== 'all' || this.sortMode !== 'received-desc';
  }

  get hasMatchingResponses(): boolean {
    return !!this.responses && this.responses.length > 0;
  }

  get hasLoadedResponses(): boolean {
    return !!this.allResponses && this.allResponses.length > 0;
  }

  hasTranscript(response: VoxResponse): boolean {
    return !!response.corrected_transcript || (response.transcript && response.transcript !== '[No speech detected]');
  }

  transcriptLabel(response: VoxResponse): string {
    if (response.corrected_transcript) {
      return 'Edited transcript';
    }
    if (this.hasTranscript(response)) {
      return 'Auto transcript';
    }
    return sttStatusLabel(response.stt_status, response.stt_error_code);
  }

  audioStatus(response: VoxResponse): 'available' | 'absent' | 'unknown' {
    if (typeof response.mimetype === 'string' && response.mimetype.trim()) {
      return 'available';
    }
    if (Object.prototype.hasOwnProperty.call(response, 'mimetype') &&
        (response.mimetype === null || response.mimetype === '')) {
      return 'absent';
    }
    return 'unknown';
  }

  audioLabel(response: VoxResponse): string {
    switch (this.audioStatus(response)) {
      case 'available':
        return 'Audio available';
      case 'absent':
        return 'No audio';
      default:
        return 'Audio status unknown';
    }
  }

  publishLabel(response: VoxResponse): string {
    if (this.isPublishing(response)) {
      return this.publishTargets[response.id] ? 'Publishing...' : 'Unpublishing...';
    }
    return response.published ? 'Published' : 'Draft';
  }

  earlier(): void {
    let before: number = 0;
    try {
      before = this.allResponses[this.allResponses.length - 1].id;
      if (!this.beforeResponses) {
        this.beforeResponses = [];
      }
      this.beforeResponses.push(this.allResponses[0].id)
    } catch (err) {
      console.log(err);
    }
    this.before = before;
    this.refreshResponses();
  }

  later(): void {
    let before: number = 0;
    try {
      before = this.beforeResponses[this.beforeResponses.length - 1] + 1;
      this.beforeResponses = this.beforeResponses.slice(0, -1);
    } catch (err) { }
    this.before = before;
    this.refreshResponses();
  }

  downloadAsCSV(): void {
    let csvData = `"received","name","location","email","phone","transcript","corrected"\n`;
    let responses = this.allResponses;
    for (let i = 0; i < responses.length; i++) {
      let r = responses[i];
      csvData += `"${r.received}","${r.name}","${r.location}","${r.email}","${r.phone}","${r.transcript}","${r.corrected_transcript}"\n`
    }

    let hiddenElement = document.createElement('a');
    hiddenElement.href = 'data:text/csv;charset=utf-8,' + encodeURI(csvData);
    hiddenElement.target = '_blank';
    hiddenElement.download = 'responses.csv';
    hiddenElement.click();
  }

}
